import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../utils/tailwind";
import { WhatsappIcon } from "../../assets/images/svg";
import { MIXPANEL_EVENTS } from "../../constants/MixPanel";
import { trackEvent } from "../../services/MixpanelService";

const { REACT_APP_WHATSAPP_NUMBER, REACT_APP_WHATSAPP_MESSAGE } = process.env;
const WHATSAPP_URL = `https://wa.me/${REACT_APP_WHATSAPP_NUMBER}?text=${REACT_APP_WHATSAPP_MESSAGE}`;

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-blue-500 text-white hover:bg-blue-600",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-neutralSoft",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-blue-200",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  (
    { className, variant, size, loading = false, asChild = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

const ThemedPrimaryButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "w-full bg-neutralStrongBlack text-white rounded-xl hover:bg-neutralDarkBlack transition-colors px-4 py-6",
        className
      )}
    >
      {children}
    </Button>
  );
};

const ThemedSecondaryButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "w-full bg-neutralSoft text-neutralstrongBlack rounded-xl hover:bg-neutralPale transition-colors px-4 py-6",
        className
      )}
    >
      {children}
    </Button>
  );
};

const SecondaryEditButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "text-sm bg-neutralSoft text-neutralstrongBlack rounded-xl hover:bg-neutralPale transition-colors",
        className
      )}
    >
      {children}
    </Button>
  );
};

const SecondaryDeleteButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "text-sm bg-bgNegative text-negative rounded-xl transition-colors",
        className
      )}
    >
      {children}
    </Button>
  );
};

const PrimaryDeleteButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "text-sm bg-negative text-white rounded-xl transition-colors px-4 py-6 w-full",
        className
      )}
    >
      {children}
    </Button>
  );
};

const WhatsappButton = ({ text, className, onPress, ...props }) => {
  const onWhatsAppClick = () => {
    trackEvent(MIXPANEL_EVENTS.WHATSAPP_CHAT_CLICK);
    onPress?.();
    window.open(WHATSAPP_URL, "_blank");
  };

  return (
    <Button
      {...props}
      className={cn(
        "w-full bg-positive text-white rounded-xl hover:bg-green-600 transition-colors px-4 py-6",
        className
      )}
      onClick={onWhatsAppClick}
    >
      <WhatsappIcon className="w-6 h-6 text-white" />
      {text}
    </Button>
  );
};

const SecondaryYellowButton = ({ children, className, ...props }) => {
  return (
    <Button
      {...props}
      className={cn(
        "w-full bg-yellow text-neutralStrongBlack rounded-xl hover:bg-yellowPale transition-colors px-4 py-6",
        className
      )}
    >
      {children}
    </Button>
  );
};

export {
  Button,
  buttonVariants,
  ThemedPrimaryButton,
  ThemedSecondaryButton,
  WhatsappButton,
  SecondaryEditButton,
  SecondaryDeleteButton,
  SecondaryYellowButton,
  PrimaryDeleteButton,
};
